import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AdminDashboard, ManagerDashboard, ContractorDashboard } from "./DashboardMockup";

const tabs = [
  { id: "admin", label: "Admin", desc: "Roster, payroll, compliance, approvals.", points: ["Contractor roster", "Payroll dashboard", "Compliance dashboard", "Approval queue"] },
  { id: "manager", label: "Manager", desc: "Team status, projects, approvals.", points: ["Team overview", "Project status", "Approval tracking"] },
  { id: "contractor", label: "Contractor", desc: "Onboarding, timers, invoices, payouts.", points: ["Onboarding wizard", "Live timer", "Invoice creation", "Payout center"] },
];

export default function Roles() {
  const [active, setActive] = useState("admin");
  const activeTab = tabs.find((t) => t.id === active);

  return (
    <section id="roles" data-testid="roles-section" className="relative py-16 sm:py-24 lg:py-36 border-t border-[#0E1A1A]">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-[11px] uppercase tracking-[0.22em] text-[#FF4F00] font-bold mb-3"
        >
          Roles
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] leading-[1.02] font-medium max-w-3xl mb-6 sm:mb-10"
        >
          Designed around how <span className="italic font-normal text-[#9BB8B8]">teams</span> actually work.
        </motion.h2>

        {/* Tab bar */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          role="tablist"
          aria-label="User role experience"
          className="flex sm:inline-flex p-1 rounded-full border border-[#122020] bg-[#080F0F] mb-8 sm:mb-10"
          data-testid="roles-tablist"
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              data-testid={`roles-tab-${t.id}`}
              aria-selected={active === t.id}
              onClick={() => setActive(t.id)}
              className={`flex-1 sm:flex-none relative px-3 sm:px-5 py-3 sm:py-2 rounded-full text-sm transition-colors text-center ${
                active === t.id ? "text-white" : "text-[#9BB8B8] hover:text-white"
              }`}
            >
              {active === t.id && (
                <motion.span
                  layoutId="role-pill"
                  className="absolute inset-0 rounded-full bg-[#0F1E1E] border border-[#152222]"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                />
              )}
              <span className="relative z-10">{t.label}</span>
            </button>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
                data-testid={`role-content-${active}`}
              >
                <div className="font-display text-3xl tracking-tight mb-3">{activeTab.label} experience</div>
                <p className="text-[#9BB8B8] mb-6">{activeTab.desc}</p>
                <ul className="space-y-2.5">
                  {activeTab.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF4F00]" />
                      <span className="text-white">{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="lg:col-span-8 relative">
            <div className="absolute -inset-8 bg-[radial-gradient(ellipse_at_center,rgba(255,79,0,0.08),transparent_70%)] blur-3xl pointer-events-none" />
            <div className="relative overflow-x-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                {active === "admin" && <AdminDashboard compact />}
                {active === "manager" && <ManagerDashboard />}
                {active === "contractor" && <ContractorDashboard />}
              </motion.div>
            </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
